import {ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {BehaviorSubject, catchError, EMPTY, Subject, takeUntil} from "rxjs";
import {FormBuilder, UntypedFormArray, UntypedFormControl, UntypedFormGroup, Validators} from "@angular/forms";
import {ValidateIfObject} from "@shared/validators/if-object.validator";
import {map} from "rxjs/operators";
import { HttpErrorResponse } from "@angular/common/http";
import {SharedService} from "@shared/shared.service";
import {BillingService} from "@shared/services/billing.service";
import {ToastrService} from "ngx-toastr";
import {TranslateService} from "@ngx-translate/core";
import {Store} from "@ngrx/store";
import {Reducers} from "@app/reducers";
import {Actions, ofType} from "@ngrx/effects";
import * as invoicesActions from '@billing/ngrx/actions/invoices.actions';
import {NameResponse} from "@services/back-office/model/nameResponse";
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {BillingInvoiceModel} from "@services/back-office/model/billingInvoiceModel";
import {BillingInvoiceHistoryModel} from "@services/back-office/model/billingInvoiceHistoryModel";

@Component({
    selector: 'app-invoice-dialog',
    templateUrl: './invoice-dialog.component.html',
    styleUrls: ['./invoice-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class InvoiceDialogComponent implements OnInit, OnDestroy {
    loading$ = new BehaviorSubject(false);
    closeDialogFlag: boolean;
    paymentForm: UntypedFormGroup;
    history: BillingInvoiceHistoryModel[] = [];
    private destroyed$ = new Subject<void>();

    amountEdit: boolean;
    amount: UntypedFormControl = new UntypedFormControl();
    comment: UntypedFormControl = new UntypedFormControl();

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: {
            invoice: BillingInvoiceModel,
            contract: NameResponse
        },
        private dialogRef: MatDialogRef<InvoiceDialogComponent>,
        private formBuilder: FormBuilder,
        private billingService: BillingService,
        private toastrService: ToastrService,
        private translateService: TranslateService,
        private store$: Store<Reducers>,
        private actions$: Actions
    ) {
    }

    ngOnInit(): void {
        if (!this.paymentForm) {
            this.initFormArray();
        }
        if (this.data.invoice?.id) {
            this.loading$.next(true);
            this.billingService.getInvoice(this.data.invoice.id).pipe(
                takeUntil(this.destroyed$),
                map((res: BillingInvoiceModel) => {
                    this.data.invoice = res;
                    this.patchForm();
                }),
                catchError((error: HttpErrorResponse) => {
                    SharedService.showErrorWithTitle(
                        this.translateService,
                        this.toastrService,
                        `responseErrors.${error.error.message}`
                    );
                    this.closeDialog(false);
                    return EMPTY;
                })
            ).subscribe();

            this.getHistory();
        }

        this.watchEditInvoiceSuccess();
        this.watchEditInvoiceFailure();
    }

    ngOnDestroy() {
        this.destroyed$.next();
        this.destroyed$.complete();
    }

    getHistory() {
        this.billingService.getInvoiceHistory(this.data.invoice.id!).pipe(
            takeUntil(this.destroyed$),
            map((res: BillingInvoiceHistoryModel[]) => {
                this.history = res ? res : [];
            }),
            catchError(() => EMPTY)
        ).subscribe();
    }

    initFormArray() {
        this.paymentForm = this.formBuilder.group({
            paymentFormControls: this.formBuilder.array([this.initSetControls()])
        });
    }

    initSetControls() {
        return this.formBuilder.group({
            payment: ['', [Validators.required, ValidateIfObject]],
        });
    }

    addFormArrayItem() {
        (this.paymentForm.controls['paymentFormControls'] as UntypedFormArray).push(this.initSetControls());
    }

    getFormArrayControlsQuantity() {
        return (this.paymentForm.get('paymentFormControls') as UntypedFormArray).controls.length;
    }

    deleteFormArrayItem(key: any) {
        (this.paymentForm.controls['paymentFormControls'] as UntypedFormArray).removeAt(key);
    }

    getControls() {
        return (this.paymentForm.get('paymentFormControls') as UntypedFormArray).controls;
    }

    /** How much of the invoice this payment closes; derived by the backend, so it is shown, not edited. */
    getCoveredAmount(key: number): number | undefined {
        const transactionId = this.getControls()[key]?.get('payment')?.value?.id;
        return this.data.invoice?.payments?.find(item => item.transaction?.id == transactionId)?.amount;
    }

    /** What the invoice still lacks: its amount less what the payments already cover. */
    getUnhandled(): number {
        const handled = this.data.invoice.handled ? this.data.invoice.handled : 0;
        return Number(((this.data.invoice.amount ? this.data.invoice.amount : 0) - handled).toFixed(2));
    }

    patchForm() {
        if (!this.paymentForm) {
            this.initFormArray();
        }

        if (this.data?.invoice?.payments?.length) {
            const difference = this.data.invoice.payments.length - this.getFormArrayControlsQuantity();
            if (difference > 0) {
                for (let i = 0; i < difference; i++) {
                    this.addFormArrayItem();
                }
            }

            const controls = this.getControls();

            for (let i = 0; i < this.data.invoice.payments.length; i++) {
                const transaction = this.data.invoice.payments[i].transaction;
                if (controls[i]) {
                    controls[i].get('payment')!.setValue(transaction ? transaction : '');
                }
            }
        }

        this.loading$.next(false);
    }

    submitForm(closeDialog?: boolean, amountEdit?: boolean) {
        if (amountEdit && !this.amount.value) {
            return;
        }

        if (amountEdit && this.amount.value && !this.comment.value) {
            this.toastrService.warning('Для редагування суми рахунку коментар обовʼязковий');
            return;
        }

        this.closeDialogFlag = !!closeDialog;
        this.loading$.next(true);

        const paymentsForm = this.paymentForm.getRawValue();
        const payments = paymentsForm?.paymentFormControls?.length
            ? paymentsForm.paymentFormControls
                .filter((item: any) => item.payment?.id)
                .map((item: any) => ({transaction: {id: item.payment.id}}))
            : [];

        const request: BillingInvoiceModel = {
            id: this.data.invoice.id,
            ...this.amountEdit && this.amount.value && {amount: this.amount.value},
            ...this.amountEdit && this.comment.value && {comment: this.comment.value},
            payments
        };

        this.store$.dispatch(new invoicesActions.EditInvoice(request));
    }

    watchEditInvoiceSuccess() {
        this.actions$.pipe(
            ofType(invoicesActions.InvoicesActionTypes.EditInvoiceSuccess),
            takeUntil(this.destroyed$)
        ).subscribe((action: invoicesActions.EditInvoiceSuccess) => {
            this.data.invoice = action.payload;
            this.closeEditAmount();

            if (this.closeDialogFlag) {
                this.closeDialog(true);
            } else {
                this.patchForm();
                this.getHistory();
            }
        });
    }

    watchEditInvoiceFailure() {
        this.actions$.pipe(
            ofType(invoicesActions.InvoicesActionTypes.EditInvoiceFailure),
            takeUntil(this.destroyed$)
        ).subscribe(() => {
            this.loading$.next(false);
        });
    }

    closeEditAmount() {
        this.amount.reset();
        this.comment.reset();
        this.amountEdit = false;
    }

    openLink() {
        if (this.data.invoice?.invoiceLink) {
            window.open(this.data.invoice.invoiceLink, '_blank');
        }
    }

    closeDialog(result: boolean, data?: any) {
        this.dialogRef.close({result, data});
    }
}
